import React, {
  type FC,
  forwardRef,
  isValidElement,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { useComposition } from '@/hooks/use-composition';
import { cn } from '@/lib/utils';

const isValid = (el: unknown): el is HTMLElement | SVGElement =>
  el instanceof HTMLElement || el instanceof SVGElement;

const ratingItemVariants = cva(
  'group-[.size-sm]:size-4 group-[.size-md]:size-6 group-[.size-lg]:size-8 cursor-pointer text-primary hover:text-primary/80',
  {
    variants: {
      selected: {
        true: 'fill-primary/80 hover:fill-primary/60',
      },
    },
    defaultVariants: {
      selected: false,
    },
  }
);

interface RatingItemProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof ratingItemVariants> {
  score: number;
  onClick?: (event: MouseEvent) => void;
}

const RatingItem = forwardRef<HTMLSlotElement, RatingItemProps>(
  ({ className, score, selected, onClick, ...props }, ref) => (
    <Slot
      data-icon
      data-score={score}
      ref={ref}
      className={cn(ratingItemVariants({ selected }), className)}
      onClick={onClick}
      data-selected={selected ?? undefined}
      {...props}
    />
  )
);
RatingItem.displayName = 'RatingItem';

const ratingVariants = cva('group flex gap-1 w-fit h-fit');

interface RatingProps extends VariantProps<typeof ratingVariants> {
  children: ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  score: number | undefined;
  onChange: (score: number) => void;
}

const Rating: FC<RatingProps> = ({
  children: _children,
  className,
  size,
  score,
  onChange,
}) => {
  const [_, _items] = useComposition(_children, RatingItem.displayName);

  const handleClick = useCallback(
    (event: MouseEvent) => {
      if (!isValid(event.target)) {
        return;
      }
      const target = event.target;
      const svg = target.closest('svg');
      if (!svg) {
        return;
      }
      const _score = parseFloat(svg.getAttribute('data-score') ?? '0');
      onChange(_score);
    },
    [onChange]
  );

  const items = useMemo(
    () =>
      (_items ?? []).map((item) => {
        if (isValidElement(item)) {
          const ratingItemProps: Pick<RatingItemProps, 'onClick' | 'selected'> =
            {
              onClick: handleClick,
              selected:
                item.props.selected !== undefined
                  ? item.props.selected
                  : item.props.score <= (score ?? -1),
            };
          return React.cloneElement(item, ratingItemProps);
        }
        return <>{item}</>;
      }),
    [_items, handleClick, score]
  );

  return (
    <div className={cn(ratingVariants(), className, `size-${size}`)}>
      {items}
    </div>
  );
};

interface RatingComposition {
  Item: typeof RatingItem;
}

const RootWithComposition: typeof Rating & RatingComposition = Object.assign(
  Rating,
  {
    Item: RatingItem,
  }
);
export { RootWithComposition as Rating };
